import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { filter, includes, map, uniq } from 'lodash';
import React from 'react';

export interface FileTreeEntry {
  id: string;
  name: string;
  entryType: 'folder' | 'file';
}

export interface FileTreeSelectorProps<TEntry extends FileTreeEntry> {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  headerAction?: React.ReactNode;
  rootEntries: TEntry[];
  selectedItemIds: string[];
  quickFilter: string;
  onQuickFilterChange: (quickFilter: string) => void;
  onSelectedItemIdsChange: (selectedItemIds: string[]) => void;
  getChildEntries: (entry: TEntry) => TEntry[];
  getSelectableItemIds: (entry: TEntry) => string[];
  getEntryItemCount?: (entry: TEntry) => number | undefined;
  onFolderExpansionChange?: (entry: TEntry, isExpanded: boolean) => void;
  renderEntryPrimary?: (entry: TEntry, isFolder: boolean) => React.ReactNode;
  renderEntrySecondary?: (entry: TEntry, isFolder: boolean) => React.ReactNode;
  renderEntrySecondaryAction?: (entry: TEntry, isFolder: boolean) => React.ReactNode;
  renderEntryStatus?: (entry: TEntry, depth: number, isExpanded: boolean) => React.ReactNode;
  searchDisabled?: boolean;
  interactionDisabled?: boolean;
  searchLabel?: string;
  searchPlaceholder?: string;
  listAriaLabel: string;
  emptyMessage?: React.ReactNode;
  noMatchesMessage?: React.ReactNode;
  selectAllLoadedLabel?: (count: number, hasFilter: boolean) => React.ReactNode;
  sx?: SxProps<Theme>;
  listSx?: SxProps<Theme>;
  defaultExpandedItemIds?: string[];
}

const defaultSelectAllLoadedLabel = (count: number, hasFilter: boolean) =>
  hasFilter ? `Select all matching loaded files (${count})` : `Select all loaded files (${count})`;

const EMPTY_EXPANDED_ITEM_IDS: string[] = [];

const FileTreeSelector = <TEntry extends FileTreeEntry>({
  title,
  subtitle,
  headerAction,
  rootEntries,
  selectedItemIds,
  quickFilter,
  onQuickFilterChange,
  onSelectedItemIdsChange,
  getChildEntries,
  getSelectableItemIds,
  getEntryItemCount,
  onFolderExpansionChange,
  renderEntryPrimary,
  renderEntrySecondary,
  renderEntrySecondaryAction,
  renderEntryStatus,
  searchDisabled = false,
  interactionDisabled = false,
  searchLabel = 'Quick filter',
  searchPlaceholder = 'Filter loaded files and folders',
  listAriaLabel,
  emptyMessage = 'This folder is empty.',
  noMatchesMessage = 'No loaded files or folders match this filter.',
  selectAllLoadedLabel = defaultSelectAllLoadedLabel,
  sx,
  listSx,
  defaultExpandedItemIds = EMPTY_EXPANDED_ITEM_IDS,
}: FileTreeSelectorProps<TEntry>) => {
  const [expandedItemIds, setExpandedItemIds] = React.useState<string[]>(defaultExpandedItemIds);
  const normalizedQuickFilter = quickFilter.trim().toLowerCase();
  const defaultExpandedItemIdsSignature = defaultExpandedItemIds.join('\n');

  React.useEffect(() => {
    setExpandedItemIds(defaultExpandedItemIds);
    // Expansion defaults should reset only when the intended default set changes,
    // not when a caller recreates an equivalent array during render.
  }, [defaultExpandedItemIdsSignature]);

  const matchesQuickFilter = (entry: TEntry) =>
    !normalizedQuickFilter || includes(`${entry.name} ${entry.id}`.toLowerCase(), normalizedQuickFilter);

  const hasMatchingDescendant = (entry: TEntry): boolean =>
    entry.entryType === 'folder' &&
    filter(getChildEntries(entry), (childEntry) => matchesQuickFilter(childEntry) || hasMatchingDescendant(childEntry))
      .length > 0;

  const getVisibleEntries = (entries: TEntry[], ancestorMatchesFilter = false): TEntry[] =>
    filter(entries, (entry) => ancestorMatchesFilter || matchesQuickFilter(entry) || hasMatchingDescendant(entry));

  const getVisibleSelectableItemIds = (entries: TEntry[], ancestorMatchesFilter = false): string[] =>
    uniq(
      entries.flatMap((entry) => {
        if (entry.entryType === 'file') {
          return ancestorMatchesFilter || matchesQuickFilter(entry) ? getSelectableItemIds(entry) : [];
        }

        const entryMatchesFilter = ancestorMatchesFilter || matchesQuickFilter(entry);
        const childEntries = getVisibleEntries(getChildEntries(entry), entryMatchesFilter);

        return childEntries.length > 0
          ? getVisibleSelectableItemIds(childEntries, entryMatchesFilter)
          : entryMatchesFilter
          ? getSelectableItemIds(entry)
          : [];
      })
    );

  const visibleEntries = getVisibleEntries(rootEntries);
  const currentVisibleItemIds = getVisibleSelectableItemIds(visibleEntries);
  const selectedCurrentVisibleCount = filter(currentVisibleItemIds, (itemId) =>
    includes(selectedItemIds, itemId)
  ).length;
  const isCurrentVisibleSelectionChecked =
    currentVisibleItemIds.length > 0 && selectedCurrentVisibleCount === currentVisibleItemIds.length;

  const toggleItemIds = (itemIds: string[]) => {
    const areAllSelected = filter(itemIds, (itemId) => !includes(selectedItemIds, itemId)).length === 0;

    onSelectedItemIdsChange(
      areAllSelected
        ? filter(selectedItemIds, (selectedItemId) => !includes(itemIds, selectedItemId))
        : uniq([...selectedItemIds, ...itemIds])
    );
  };

  const toggleEntry = (entry: TEntry) => {
    const itemIds = getSelectableItemIds(entry);

    if (itemIds.length > 0) {
      toggleItemIds(itemIds);
    }
  };

  const toggleCurrentVisibleSelection = () => toggleItemIds(currentVisibleItemIds);

  const toggleFolderExpansion = (entry: TEntry) => {
    let nextExpanded = false;

    setExpandedItemIds((currentItemIds) => {
      nextExpanded = !includes(currentItemIds, entry.id);

      return nextExpanded
        ? [...currentItemIds, entry.id]
        : filter(currentItemIds, (currentItemId) => currentItemId !== entry.id);
    });
    onFolderExpansionChange?.(entry, nextExpanded);
  };

  const renderEntries = (entries: TEntry[], depth = 0, ancestorMatchesFilter = false): React.ReactNode =>
    map(getVisibleEntries(entries, ancestorMatchesFilter), (entry) => {
      const isFolder = entry.entryType === 'folder';
      const isExpanded = includes(expandedItemIds, entry.id);
      const selectableItemIds = getSelectableItemIds(entry);
      const selectedCount = filter(selectableItemIds, (itemId) => includes(selectedItemIds, itemId)).length;
      const isChecked = selectableItemIds.length > 0 && selectedCount === selectableItemIds.length;
      const isIndeterminate = selectedCount > 0 && selectedCount < selectableItemIds.length;
      const isDisabled = interactionDisabled || selectableItemIds.length === 0;
      const entryMatchesFilter = ancestorMatchesFilter || matchesQuickFilter(entry);
      const childEntries = getChildEntries(entry);
      const secondaryAction = renderEntrySecondaryAction?.(entry, isFolder);
      const itemCount = getEntryItemCount?.(entry);
      const itemCountLabel = itemCount == null ? null : `${itemCount} ${itemCount === 1 ? 'file' : 'files'}`;

      return (
        <React.Fragment key={entry.id}>
          <ListItem divider disablePadding secondaryAction={secondaryAction}>
            <ListItemButton
              disabled={isDisabled}
              onClick={() => toggleEntry(entry)}
              sx={{ pl: 1 + depth * 2, pr: secondaryAction ? 10 : 2, minHeight: 56 }}
            >
              <Box sx={{ width: 32, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                {isFolder ? (
                  <IconButton
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${entry.name}`}
                    size="small"
                    disabled={interactionDisabled}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleFolderExpansion(entry);
                    }}
                  >
                    {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  </IconButton>
                ) : null}
              </Box>
              <Checkbox
                edge="start"
                checked={isChecked}
                disabled={isDisabled}
                indeterminate={isIndeterminate}
                tabIndex={-1}
                disableRipple
                inputProps={{ 'aria-label': `Select ${entry.name}` }}
              />
              <ListItemIcon sx={{ minWidth: 36 }}>
                {isFolder ? <FolderOutlinedIcon /> : <InsertDriveFileOutlinedIcon />}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
                    {renderEntryPrimary ? (
                      renderEntryPrimary(entry, isFolder)
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: isFolder ? 600 : 500 }} noWrap>
                        {entry.name}
                      </Typography>
                    )}
                    {itemCountLabel ? <Chip size="small" label={itemCountLabel} /> : null}
                  </Box>
                }
                secondary={renderEntrySecondary?.(entry, isFolder)}
                primaryTypographyProps={{ component: 'div' }}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </ListItemButton>
          </ListItem>
          {renderEntryStatus?.(entry, depth, isExpanded)}
          {isFolder && isExpanded ? (
            <List disablePadding>{renderEntries(childEntries, depth + 1, entryMatchesFilter)}</List>
          ) : null}
        </React.Fragment>
      );
    });

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      {(title || subtitle || headerAction) && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              {title ? <Typography variant="subtitle2">{title}</Typography> : null}
              {subtitle ? (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            {headerAction}
          </Box>
          <TextField
            fullWidth
            type="search"
            size="small"
            label={searchLabel}
            placeholder={searchPlaceholder}
            value={quickFilter}
            disabled={searchDisabled}
            onChange={(event) => onQuickFilterChange(event.target.value)}
            sx={{ mt: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: quickFilter ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={`Clear ${searchLabel.toLowerCase()}`}
                    size="small"
                    disabled={searchDisabled}
                    onClick={() => onQuickFilterChange('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
      )}
      {rootEntries.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {emptyMessage}
        </Typography>
      ) : visibleEntries.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {noMatchesMessage}
        </Typography>
      ) : (
        <>
          <FormControlLabel
            sx={{ px: 2, my: 0.5 }}
            control={
              <Checkbox
                disabled={interactionDisabled}
                checked={isCurrentVisibleSelectionChecked}
                indeterminate={selectedCurrentVisibleCount > 0 && !isCurrentVisibleSelectionChecked}
                onChange={toggleCurrentVisibleSelection}
              />
            }
            label={selectAllLoadedLabel(currentVisibleItemIds.length, Boolean(normalizedQuickFilter))}
          />
          <List
            disablePadding
            aria-label={listAriaLabel}
            sx={{ borderTop: 1, borderColor: 'divider', overflowY: 'auto', ...listSx }}
          >
            {renderEntries(visibleEntries)}
          </List>
        </>
      )}
    </Box>
  );
};

export default FileTreeSelector;
